/**
 * BYO v2 — edit-source hook (focused per-node modal).
 *
 * Thin orchestrator over the pieces that hydrate the controlled `byo/v2`
 * source panels (InputPanel + CodeEditor + PackagesPanel) from an EXISTING BYO
 * (`flow_type: custom`) source id and persist edits back:
 *
 *   - {@link useByoSourceHydration}        load the source, credential + container ids
 *   - {@link useByoCodeContainerHydration} load/decode the container, packages,
 *                                          runtime vars, perf config, tab classification
 *   - {@link useByoProcessorPicker}        "Use Existing Function" fetch + preview
 *   - {@link useByoSourceSave}             save() + orphaned-container rollback
 *
 * This hook adds only the unsaved-changes tracking and the combined return
 * shape the modal consumes.
 *
 * NO runtime/cluster, NO scheduling, NO nexset — same constraints as the rest of
 * byo/v2.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAccessToken } from 'js/reducers/session/selectors';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import useSourcesAdapter from 'new/state/sources/adapter/useSourcesAdapter';
import type { ByoInputValue } from '../types';
import type {
  BYOPerformanceConfig,
  ByoCodeMode,
  RuntimeVar,
} from 'new/components/flows/types/byo/core/model';
import { useByoSourceHydration } from './useByoSourceHydration';
import { useByoCodeContainerHydration } from './useByoCodeContainerHydration';
import { useByoProcessorPicker } from './useByoProcessorPicker';
import { useByoSourceSave } from './useByoSourceSave';

export interface UseByoEditSource {
  /** True until the source + code container have hydrated. */
  loading: boolean;
  /** Hydration error message, if any. */
  error: string | null;
  /** Source or code-container load failed — Save must stay disabled. */
  hydrationFailed: boolean;
  /** Credential verification failed; non-blocking since the code still loaded. */
  credentialError: string | null;
  /**
   * True while a "Use Existing Function" pick has been made but its
   * container hasn't been fetched yet (it then previews via `predefined*`,
   * never via `code`/`packages` — NEX-22636). Save must be
   * blocked during this window — confirmed bug, fixed 2026-08-20: nothing
   * previously gated Save here, so a fast pick-then-Save persisted whatever
   * `code`/`packages` were left over from before the pick (e.g. the previous
   * selection, or empty) while `ui.byo_code_predefined_id` still recorded the
   * new pick — the flow would run the wrong code under the right label.
   */
  processorPickPending: boolean;
  /**
   * Fetching the picked "Use Existing Function" container failed. The pick is
   * cleared (so Save stays blocked by the nothing-picked gate) and the user
   * can pick again. Cleared on the next pick.
   */
  pickError: string | null;
  /**
   * The picked function's own code / packages / extra_data, for PREVIEW and
   * test runs only. Deliberately kept apart from `code`/`packages` (the
   * user's own-code draft) — see the pick effect below.
   */
  predefinedCode: string;
  predefinedPackages: string[];
  predefinedExtraData: string;
  /** True once the user has changed anything since hydration (unsaved-changes guard). */
  isDirty: boolean;

  /** Flow/source name shown in the header. */
  name: string;
  setName: (name: string) => void;

  // --- Source (InputPanel) ---
  input: ByoInputValue;
  setInput: (next: ByoInputValue) => void;
  /** The hydrated source's own source_config — passed through unchanged to the
   * test-run probe payload so it matches whatever context the backend expects. */
  sourceConfig: any;

  // --- Code (CodeEditor + PackagesPanel) ---
  code: string;
  setCode: (code: string) => void;
  codeMode: ByoCodeMode;
  setCodeMode: (mode: ByoCodeMode) => void;
  predefinedId: number | string | null;
  setPredefinedId: (id: number | string | null) => void;
  packages: string[];
  setPackages: (next: string[]) => void;
  runtimeVars: RuntimeVar[];
  setRuntimeVars: (next: RuntimeVar[]) => void;
  performanceConfig: BYOPerformanceConfig;
  setPerformanceConfig: (next: BYOPerformanceConfig) => void;

  /** Bearer token for AI affordances in the editor. */
  token: string;

  // --- Persistence ---
  isSaving: boolean;
  saveError: string | null;
  /** True when code has been explicitly saved at least once (source_config flag). */
  codeConfigured: boolean;
  /** Update the source + code container; resolves to the updated source. */
  save: (opts?: { markCodeConfigured?: boolean }) => Promise<any>;
}

/**
 * @param id BYO source id to hydrate from. When absent the hook returns an empty
 *           (create-like) draft so the modal still renders.
 */
export function useByoEditSource(
  id?: number,
  { viewOnly = false }: { viewOnly?: boolean } = {},
): UseByoEditSource {
  const dispatch = useAppDispatch();
  const token = useSelector(getAccessToken) ?? '';
  const { requestUpdateSourceById } = useSourcesAdapter();

  const {
    loading,
    error,
    hydrationFailed,
    credentialError,
    source,
    name,
    setName,
    input,
    setInput,
    codeContainerId,
    setCodeContainerId,
  } = useByoSourceHydration(id, { viewOnly });

  const {
    container,
    contentDecoded,
    attachedIsReusableRef,
    code,
    setCode,
    codeMode,
    setCodeMode,
    predefinedId,
    setPredefinedId,
    packages,
    setPackages,
    runtimeVars,
    setRuntimeVars,
    performanceConfig,
    setPerformanceConfig,
  } = useByoCodeContainerHydration({ id, codeContainerId, loading });

  const { processorPickPending, pickError, predefinedPreview } = useByoProcessorPicker({
    predefinedId,
    setPredefinedId,
  });

  // `performanceConfig` state always starts populated with BYO_PERF_DEFAULTS
  // regardless of whether the panel is even shown — persisting it
  // unconditionally would write runtime_config to the backend on every save
  // even for an org where the panel is hidden. Gate the WRITE the same way
  // StudioCodeStep.tsx gates the panel's visibility; `undefined` makes
  // mergeExtraData (services.ts) skip it entirely. This exact fix existed
  // in useStudioDraft.ts's now-removed dead create-path code but was never
  // ported to this, the actually-live save path. Confirmed bug, fixed
  // 2026-08-20.
  const showPerfConfig = useFeatureFlag('UI_BYO_PERFORMANCE_CONFIG');

  // ── Unsaved-changes tracking ───────────────────────────────────────────────
  // Snapshot the draft once hydration has settled; dirty = differs from it.
  const draftSnapshot = JSON.stringify({
    code,
    codeMode,
    predefinedId,
    packages,
    runtimeVars,
    performanceConfig: showPerfConfig ? performanceConfig : null,
  });
  const [baselineSnapshot, setBaselineSnapshot] = useState<string | null>(null);
  const hydrationSettled = !loading && (codeContainerId == null || contentDecoded);
  useEffect(() => {
    setBaselineSnapshot(null);
  }, [id]);
  useEffect(() => {
    if (hydrationSettled && baselineSnapshot == null) setBaselineSnapshot(draftSnapshot);
  }, [hydrationSettled, baselineSnapshot, draftSnapshot]);
  const isDirty = baselineSnapshot != null && baselineSnapshot !== draftSnapshot;

  const { save, isSaving, saveError } = useByoSourceSave({
    id,
    input,
    code,
    codeMode,
    predefinedId,
    packages,
    runtimeVars,
    performanceConfig,
    showPerfConfig,
    name,
    source,
    codeContainerId,
    attachedIsReusableRef,
    dispatch,
    requestUpdateSourceById,
    setCodeContainerId,
    hydrationFailed,
    // The attached container's saved extra_data — merge base for an in-place
    // update so keys the UI doesn't model survive the save (NEX-22636).
    baseExtraData: container?.code_config?.extra_data ?? '',
  });

  const codeConfigured = !!source?.source_config?.['ui.byo_code_configured'];

  return useMemo(
    () => ({
      loading,
      error,
      hydrationFailed,
      credentialError,
      processorPickPending,
      pickError,
      predefinedCode: predefinedPreview.code,
      predefinedPackages: predefinedPreview.packages,
      predefinedExtraData: predefinedPreview.extraData,
      isDirty,
      name,
      setName,
      input,
      setInput,
      sourceConfig: source?.source_config ?? {},
      code,
      setCode,
      codeMode,
      setCodeMode,
      predefinedId,
      setPredefinedId,
      packages,
      setPackages,
      runtimeVars,
      setRuntimeVars,
      performanceConfig,
      setPerformanceConfig,
      token,
      isSaving,
      saveError,
      save,
      codeConfigured,
    }),
    [
      loading,
      error,
      hydrationFailed,
      credentialError,
      processorPickPending,
      pickError,
      predefinedPreview,
      isDirty,
      name,
      input,
      source,
      code,
      codeMode,
      predefinedId,
      packages,
      runtimeVars,
      performanceConfig,
      token,
      isSaving,
      saveError,
      save,
      codeConfigured,
      // useState setters from the sub-hooks — stable, listed only because
      // the linter can't see that across a hook boundary.
      setName,
      setInput,
      setCode,
      setCodeMode,
      setPredefinedId,
      setPackages,
      setRuntimeVars,
      setPerformanceConfig,
    ],
  );
}

export default useByoEditSource;
